import React from 'react';
import { Link, Navigate, useParams, useLocation } from 'react-router-dom';
import { useI18n } from '../i18n/LanguageContext';
import { getProductsForCategory } from '../data/productCatalog';
import siteImageManifest from '../data/siteImageManifest.generated.json';
import SimilarProductsCarousel from '../components/SimilarProductsCarousel';
import { PET_PRODUCTS } from '../data/petProducts';

function formatNumber(n) {
  if (typeof n !== 'number') return '';
  if (Number.isInteger(n)) return String(n);
  return String(n).replace('.', ',');
}

function formatLitersFromMl(ml) {
  if (typeof ml !== 'number') return '';
  const liters = ml / 1000;
  const s = Number.isInteger(liters) ? String(liters) : String(liters).replace(/0+$/g, '').replace(/\.$/, '');
  return `${s}L`;
}

function formatCapTitleFromId(id, lang) {
  const raw = String(id || '')
    .replace(/^cap-/, '')
    .replace(/^dop-/, '')
    .replace(/\bdop\b/gi, '')
    .replace(/-+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (!raw) return '';
  if (lang !== 'en') return raw;

  return raw
    .split(' ')
    .filter(Boolean)
    .map((w) => {
      const upper = w.toUpperCase();
      if (upper === 'PCO') return 'PCO';
      if (/^\d+$/.test(w) || /^\d+\/\d+$/.test(w)) return w;
      return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
    })
    .join(' ');
}

export default function GenericProduct() {
  const { t, lang } = useI18n();
  const { category, productId } = useParams();
  const location = useLocation();

  const list = React.useMemo(() => getProductsForCategory(category), [category]);
  const product = React.useMemo(() => (Array.isArray(list) ? list.find((p) => p.id === productId) : null), [list, productId]);
  const isCapsCategory = category === 'capace-manere';
  const isOtherProductsCategory = category === 'galeti-bidoane';
  const useCompactImage = isCapsCategory || isOtherProductsCategory;
  const neckTypesLabel = React.useMemo(() => {
    const ids = Array.isArray(product?.neckTypes) ? product.neckTypes : [];
    if (!ids.length) return '';
    const labels = ids
      .map((id) => {
        const key = `products.filters.options.neckTypes.${id}`;
        const translated = t(key);
        return translated === key ? String(id) : translated;
      })
      .filter(Boolean);
    return labels.join(', ');
  }, [product, t]);

  const titleKey = product?.i18nKey || '';
  const resolvedTitle = React.useMemo(() => {
    if (!titleKey) return '';

    const directKey = `${titleKey}.title`;
    const direct = t(directKey);
    if (direct !== directKey) return direct;

    if (titleKey.startsWith('products.caps.')) {
      const suffix = titleKey.slice('products.caps.'.length);
      const altKey = `products.pet.caps.${suffix}.title`;
      const alt = t(altKey);
      if (alt !== altKey) return alt;
    }

    return '';
  }, [titleKey, t]);
  const resolvedDesc = titleKey ? t(`${titleKey}.desc`) : '';
  const title = resolvedTitle || product?.title || (isCapsCategory ? formatCapTitleFromId(product?.id, lang) : product?.id || '');
  const desc = resolvedDesc && resolvedDesc !== `${titleKey}.desc` ? resolvedDesc : (product?.desc || '');

  const v = typeof siteImageManifest?.generatedAt === 'string' ? siteImageManifest.generatedAt : '';
  const suffix = v ? `?v=${encodeURIComponent(v)}` : '';

  const images = React.useMemo(() => {
    const raw = Array.isArray(product?.images) ? product.images : [];
    if (!raw.length) return [`${process.env.PUBLIC_URL}/brand/yk-mark.svg`];
    return raw.map((src) => `${process.env.PUBLIC_URL}${src}${suffix}`);
  }, [product, suffix]);

  const [selectedImageIndex, setSelectedImageIndex] = React.useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = React.useState(false);
  React.useEffect(() => {
    setSelectedImageIndex(0);
  }, [product?.id]);

  const closeLightbox = React.useCallback(() => setIsLightboxOpen(false), []);
  const openLightbox = React.useCallback(() => setIsLightboxOpen(true), []);

  const hasProductPhotos = Array.isArray(product?.images) && product.images.length > 0;
  const safeImageIndex = Math.min(selectedImageIndex, images.length - 1);
  const imgSrc = images[safeImageIndex];
  const canCyclePhotos = images.length > 1;
  const canUseLightbox = (isCapsCategory || isOtherProductsCategory) && hasProductPhotos;

  const prevLabel = t('common.prev') === 'common.prev' ? 'Previous photo' : t('common.prev');
  const nextLabel = t('common.next') === 'common.next' ? 'Next photo' : t('common.next');

  function showPrevPhoto() {
    if (!canCyclePhotos) return;
    setSelectedImageIndex((idx) => (idx - 1 + images.length) % images.length);
  }

  function showNextPhoto() {
    if (!canCyclePhotos) return;
    setSelectedImageIndex((idx) => (idx + 1) % images.length);
  }

  React.useEffect(() => {
    if (!isLightboxOpen) return;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        closeLightbox();
        return;
      }
      if (canCyclePhotos && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
        e.preventDefault();
        if (e.key === 'ArrowLeft') showPrevPhoto();
        else showNextPhoto();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [isLightboxOpen, canCyclePhotos, closeLightbox, showPrevPhoto, showNextPhoto]);

  const volumeMl = product?.volumeMl || product?.fillVolumeMl || product?.volume || null;
  const volumeLabel = product?.volumeLabel || '';
  const shapeLabel = product?.shape ? t(`products.filters.options.shapes.${product.shape}`) : '';

  const capsColorLabel = React.useMemo(() => {
    const isChildProofPco1810 = product?.id === 'cap-dop-child-pco-1810';
    const isWhite = product?.id === 'cap-trigger' || product?.id === 'cap-pompita-sapun';
    if (isChildProofPco1810) return lang === 'en' ? 'Red' : 'Roșu';
    if (lang === 'en') return isWhite ? 'White' : 'Various colors';
    return isWhite ? 'Alb' : 'Diverse culori';
  }, [product?.id, lang]);

  const otherProductsColorLabel = React.useMemo(() => {
    if (!isOtherProductsCategory) return '';
    const color = String(product?.color || '').toLowerCase();
    if (color === 'white') return lang === 'en' ? 'White' : 'Alb';
    if (color === 'transparent') return lang === 'en' ? 'Transparent' : 'Transparent';
    if (color === 'gray' || color === 'grey') return lang === 'en' ? 'Grey' : 'Gri';
    if (color === 'natur' || color === 'natural') return lang === 'en' ? 'Natural' : 'Natur';
    return '-';
  }, [isOtherProductsCategory, product?.color, lang]);

  const hideCapNotice =
    product?.id === 'cap-trigger' || product?.id === 'cap-pompita-sapun' || product?.id === 'cap-dop-child-pco-1810';
  const showCapNotice = isCapsCategory && !hideCapNotice;

  const capNoticeText =
    lang === 'en'
      ? 'This model is available in a variety of colors. Contact us so we can provide caps in the color needed for your product.'
      : 'Acest model este disponibil intr-o varietate de culori. Contacteaza-ne pentru a-ti oferi dopurile pe culoarea necesara produsului tau.';

  const capNeckTypes = React.useMemo(() => {
    const base = Array.isArray(product?.neckTypes) && product.neckTypes.length ? product.neckTypes : [];
    if (base.length) return base;
    if (product?.id === 'cap-pompita-sapun' || product?.id === 'cap-trigger') return ['pco1810'];
    return [];
  }, [product]);

  const compatibleSticleItems = React.useMemo(() => {
    if (!isCapsCategory || !capNeckTypes.length) return [];
    const set = new Set(capNeckTypes);
    const items = [];

    for (const g of PET_PRODUCTS) {
      for (const v of g.variants) {
        const vNecks = Array.isArray(v.neckTypes) ? v.neckTypes : [];
        if (!vNecks.some((n) => set.has(n))) continue;
        const hasPhotos = Array.isArray(v.images) && v.images.length;
        const imgSrc = hasPhotos ? v.images[0] : `${process.env.PUBLIC_URL}/brand/yk-mark.svg`;
        const title = v.i18nKey ? (t(`${v.i18nKey}.title`) !== `${v.i18nKey}.title` ? t(`${v.i18nKey}.title`) : (g.displayName || g.groupKey || v.id)) : (g.displayName || g.groupKey || v.id);
        items.push({ to: `/produse/sticle-pet/${v.id}`, title, imgSrc, isPhoto: Boolean(hasPhotos) });
      }
    }

    return items.slice(0, 12);
  }, [isCapsCategory, capNeckTypes, t]);

  const similarOtherItems = React.useMemo(() => {
    if (!isOtherProductsCategory || !product) return [];

    const baseType = String(product.type || '').trim();
    if (!baseType) return [];

    let allowedTypes = [];
    if (baseType === 'caserola') allowedTypes = ['caserola'];
    else if (baseType === 'galeata') allowedTypes = ['galeata'];
    else if (baseType === 'bidon' || baseType === 'canistra') allowedTypes = ['bidon', 'canistra'];
    else allowedTypes = [baseType];

    return (Array.isArray(list) ? list : [])
      .filter((p) => p && p.id !== product.id && allowedTypes.includes(p.type))
      .slice(0, 12)
      .map((p) => {
        const photos = Array.isArray(p.images) ? p.images : [];
        const hasPhotos = photos.length > 0;
        const selectedImage = hasPhotos ? photos[0] : '';
        const imgSrc = selectedImage
          ? `${process.env.PUBLIC_URL}${selectedImage}${suffix}`
          : `${process.env.PUBLIC_URL}/brand/yk-mark.svg`;

        const key = `${p.i18nKey}.title`;
        const translated = t(key);
        const title = translated !== key ? translated : (p.title || p.id);

        return {
          to: `/produse/${category}/${p.id}`,
          title,
          imgSrc,
          isPhoto: Boolean(hasPhotos),
        };
      });
  }, [isOtherProductsCategory, product, list, suffix, t, category]);

  if (!Array.isArray(list) || !list.length) return <Navigate to="/produse" replace />;
  if (!product) return <Navigate to={`/produse/${category}`} replace />;

  const params = new URLSearchParams(location.search || '');
  params.set('category', category);
  params.set('productId', product.id);
  params.set('productTitle', title);
  if (volumeMl) params.set('volumeMl', String(volumeMl));
  if (product.type) params.set('type', product.type);
  if (Array.isArray(product.neckTypes) && product.neckTypes.length) params.set('neckTypes', product.neckTypes.join(','));
  const contactUrl = `/contact?${params.toString()}`;

  const hasTechSheet = product.hasTechSheet !== false;
  const showTechSheet = hasTechSheet && !isCapsCategory && !isOtherProductsCategory;
  const techSheetUrl = `${process.env.PUBLIC_URL}/tech-sheets/${category}/${product.id}.pdf`;
  const similarTitle = t('products.pet.similarTitle') === 'products.pet.similarTitle' ? 'Produse similare' : t('products.pet.similarTitle');

  return (
    <section className="yk-page yk-pageProductDetail">
      <h1 className="yk-productDetailTitle">{title}</h1>

      <div className="yk-productDetailLayout">
        <div className="yk-productDetailImage">
          <div
            className={canCyclePhotos ? 'yk-productDetailPhotoWrap is-photo has-arrows' : 'yk-productDetailPhotoWrap is-photo'}
            style={{ minHeight: useCompactImage ? 'auto' : 360 }}
          >
            {canCyclePhotos ? (
              <button
                type="button"
                className="yk-productDetailPhotoArrow is-left"
                aria-label={prevLabel}
                onClick={showPrevPhoto}
              >
                ‹
              </button>
            ) : null}

            <div className="yk-productImgWrap" style={{ width: useCompactImage ? 220 : 'auto' }}>
              <img
                className={useCompactImage ? 'yk-productImg is-photo' : 'yk-productDetailImg is-photo'}
                src={imgSrc}
                alt={title}
                loading="eager"
                decoding="async"
                role={canUseLightbox ? 'button' : undefined}
                tabIndex={canUseLightbox ? 0 : undefined}
                onClick={canUseLightbox ? openLightbox : undefined}
                onKeyDown={
                  canUseLightbox
                    ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          openLightbox();
                        }
                      }
                    : undefined
                }
                style={canUseLightbox ? { cursor: 'zoom-in' } : undefined}
              />
            </div>

            {canCyclePhotos ? (
              <button
                type="button"
                className="yk-productDetailPhotoArrow is-right"
                aria-label={nextLabel}
                onClick={showNextPhoto}
              >
                ›
              </button>
            ) : null}
          </div>

          {!isCapsCategory && (volumeLabel || volumeMl) ? (
            <div className="yk-volumeRow" role="navigation" aria-label={t('products.pet.specs.fillVolume')}>
              <span className="yk-btn yk-btnGhost is-active">{volumeLabel || formatLitersFromMl(volumeMl)}</span>
            </div>
          ) : null}
        </div>

        <aside className="yk-productDetailSpecs yk-card">
          {isOtherProductsCategory ? (
            <>
              <div className="yk-specRow">
                <div className="yk-specLabel">{lang === 'en' ? 'Volume' : 'Volum'}</div>
                <div className="yk-specValue">{volumeLabel || (volumeMl ? `${formatNumber(volumeMl)} ml` : '-')}</div>
              </div>
              <div className="yk-specRow">
                <div className="yk-specLabel">{t('products.pet.specs.color')}</div>
                <div className="yk-specValue">{otherProductsColorLabel || '-'}</div>
              </div>
              <div className="yk-specRow">
                <div className="yk-specLabel">{t('products.pet.specs.shape')}</div>
                <div className="yk-specValue">{shapeLabel || '-'}</div>
              </div>
            </>
          ) : !isCapsCategory ? (
            <>
              <div className="yk-specRow">
                <div className="yk-specLabel">{t('products.pet.specs.fillVolume')}</div>
                <div className="yk-specValue">{volumeMl ? `${formatNumber(volumeMl)} ml` : '-'}</div>
              </div>

              <div className="yk-specRow">
                <div className="yk-specLabel">{t('products.pet.specs.shape')}</div>
                <div className="yk-specValue">{shapeLabel || '-'}</div>
              </div>
            </>
          ) : null}

          {!isOtherProductsCategory ? (
            <div className="yk-specRow">
              <div className="yk-specLabel">{t('products.pet.specs.neckType')}</div>
              <div className="yk-specValue">{neckTypesLabel || '-'}</div>
            </div>
          ) : null}

          {isCapsCategory ? (
            <div className="yk-specRow">
              <div className="yk-specLabel">{t('products.pet.specs.color')}</div>
              <div className="yk-specValue">{capsColorLabel}</div>
            </div>
          ) : null}

          {product.type && !isOtherProductsCategory ? (
            <div className="yk-specRow">
              <div className="yk-specLabel">{t('products.filters.options.shapes.type') || 'Tip'}</div>
              <div className="yk-specValue">{product.type}</div>
            </div>
          ) : null}

          {showCapNotice ? (
            <div className="yk-capNotice" role="note" aria-label="Informație culori dopuri">
              <span className="yk-capNoticeIcon" aria-hidden="true">
                !
              </span>
              <span className="yk-capNoticeText">
                {capNoticeText}
              </span>
            </div>
          ) : null}

          <div style={{ marginTop: 14 }} className="yk-ctaRow">
            <Link className="yk-btn yk-btnPrimary" to={contactUrl}>
              {t('products.detail.ctaButton')}
            </Link>

            {showTechSheet ? (
              <a className="yk-btn yk-btnGhost" href={techSheetUrl} download={`${product.id}.pdf`} target="_blank" rel="noreferrer">
                {t('products.pet.downloadTechSheet')}
              </a>
            ) : null}
          </div>
        </aside>

        <div className="yk-productDetailMain" style={isCapsCategory || isOtherProductsCategory ? { gridColumn: '1 / -1' } : undefined}>
          {isCapsCategory && compatibleSticleItems.length ? (
            <SimilarProductsCarousel title={`Sticle compatibile cu ${title}`} items={compatibleSticleItems} />
          ) : isOtherProductsCategory && similarOtherItems.length ? (
            <SimilarProductsCarousel title={similarTitle} items={similarOtherItems} />
          ) : (
            <div className="yk-card">
              <p className="yk-text">{desc}</p>
            </div>
          )}
        </div>
      </div>

      {isLightboxOpen && canUseLightbox ? (
        <div className="yk-lightboxOverlay" role="dialog" aria-modal="true" aria-label={title} onClick={closeLightbox}>
          <div className="yk-lightbox" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="yk-lightboxClose" aria-label="Close" onClick={closeLightbox}>
              ×
            </button>

            <img className="yk-lightboxImg" src={imgSrc} alt={title} />

            {canCyclePhotos ? (
              <div className="yk-lightboxNav" role="group" aria-label="Photo navigation">
                <button type="button" className="yk-lightboxArrow" aria-label={prevLabel} onClick={showPrevPhoto}>
                  ‹
                </button>
                <button type="button" className="yk-lightboxArrow" aria-label={nextLabel} onClick={showNextPhoto}>
                  ›
                </button>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </section>
  );
}
